import { Button } from '@/components/ui/button';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useActiveExperienceVariant } from '@/hooks/useActiveExperienceVariant';
import { useHolidayMenusLive } from '@/hooks/useHolidayMenusLive';
import { useHolidayNotice } from '@/hooks/useHolidayNotice';
import { useNewMenuLive } from '@/hooks/useNewMenuLive';
import { cn } from '@/lib/utils';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const AUTOPLAY_INTERVAL = 5000;
const WAGYU_SLIDE_IMAGE = '/img/location/IMG_8110.jpg';
const RUBIA_SLIDE_IMAGE = '/img/IMG_0372.jpeg';
const NEW_MENU_SLIDE_IMAGE = '/img/DSC09190 (2024-04-22T17_54_40.682).JPG';
const HOLIDAY_MENUS_SLIDE_IMAGE = '/img/DSC09291.JPG';

export const Hero = () => {
  const { t } = useTranslation(['home', 'common']);
  const isHoliday = useHolidayNotice();
  const showHolidayMenusSlide = useHolidayMenusLive();
  const showNewMenuSlide = useNewMenuLive();
  const activeVariant = useActiveExperienceVariant();
  const showExperienceSlide = !isHoliday && activeVariant !== null;
  const experienceVariant = activeVariant ?? 'legacy';
  const experienceSlideImage = activeVariant === 'rubia' ? RUBIA_SLIDE_IMAGE : WAGYU_SLIDE_IMAGE;
  const experienceKey = (key: string) => t(`hero.wagyu.${experienceVariant}.${key}`);
  const newMenuSlideIndex = showHolidayMenusSlide ? 1 : 0;
  const experienceSlideIndex = newMenuSlideIndex + (showNewMenuSlide ? 1 : 0);
  const slideCount = 1 + (showHolidayMenusSlide ? 1 : 0) + (showNewMenuSlide ? 1 : 0) + (showExperienceSlide ? 1 : 0);
  const hasMultipleSlides = slideCount > 1;
  const videoRef = useRef<HTMLVideoElement>(null);
  const unlockVideoRef = useRef<HTMLVideoElement>(null);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: hasMultipleSlides, align: 'start' });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    onSelect();
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi || !hasMultipleSlides) return;
    let timer = window.setInterval(() => emblaApi.scrollNext(), AUTOPLAY_INTERVAL);
    const resetTimer = () => {
      window.clearInterval(timer);
      timer = window.setInterval(() => emblaApi.scrollNext(), AUTOPLAY_INTERVAL);
    };
    emblaApi.on('pointerDown', resetTimer);
    return () => {
      window.clearInterval(timer);
      emblaApi.off('pointerDown', resetTimer);
    };
  }, [emblaApi, hasMultipleSlides]);

  useEffect(() => {
    const video = videoRef.current;
    const unlockVideo = unlockVideoRef.current;
    if (!video || !unlockVideo) return;

    video.setAttribute('webkit-playsinline', 'true');
    video.setAttribute('playsinline', 'true');
    video.setAttribute('x-webkit-airplay', 'allow');
    video.muted = true;
    video.playsInline = true;

    unlockVideo.setAttribute('webkit-playsinline', 'true');
    unlockVideo.setAttribute('playsinline', 'true');
    unlockVideo.muted = true;
    unlockVideo.playsInline = true;

    let hasPlayed = false;

    const unlockVideoPlayback = async () => {
      if (hasPlayed) return;
      try {
        unlockVideo.currentTime = 0;
        await unlockVideo.play();
        unlockVideo.pause();
        video.currentTime = 0;
        const playPromise = video.play();
        if (playPromise !== undefined) {
          await playPromise;
          hasPlayed = true;
          video.controls = false;
        }
      } catch {
        try {
          const playPromise = video.play();
          if (playPromise !== undefined) {
            await playPromise;
            hasPlayed = true;
            video.controls = false;
          }
        } catch {
          /* ignore */
        }
      }
    };

    const handleVideoReady = async () => {
      if (!hasPlayed) await unlockVideoPlayback();
    };

    const handleUserInteraction = () => {
      if (!hasPlayed) unlockVideoPlayback();
    };

    video.addEventListener('loadedmetadata', handleVideoReady);
    video.addEventListener('loadeddata', handleVideoReady);
    video.addEventListener('canplay', handleVideoReady);
    video.addEventListener('canplaythrough', handleVideoReady);

    const interactionEvents = [
      'touchstart', 'touchend', 'touchmove', 'touchcancel',
      'mousedown', 'mouseup', 'click', 'scroll', 'wheel',
      'keydown', 'keyup', 'pointerdown', 'pointerup',
    ];

    interactionEvents.forEach((event) => {
      document.addEventListener(event, handleUserInteraction, { passive: true });
      window.addEventListener(event, handleUserInteraction, { passive: true });
    });

    const handleVisibilityChange = () => {
      if (!document.hidden && !hasPlayed) unlockVideoPlayback();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    setTimeout(() => unlockVideoPlayback(), 100);

    if (document.readyState === 'complete') {
      unlockVideoPlayback();
    } else {
      window.addEventListener('load', unlockVideoPlayback, { once: true });
    }

    return () => {
      video.removeEventListener('loadedmetadata', handleVideoReady);
      video.removeEventListener('loadeddata', handleVideoReady);
      video.removeEventListener('canplay', handleVideoReady);
      video.removeEventListener('canplaythrough', handleVideoReady);
      interactionEvents.forEach((event) => {
        document.removeEventListener(event, handleUserInteraction);
        window.removeEventListener(event, handleUserInteraction);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('load', unlockVideoPlayback);
    };
  }, []);

  return (
    <section
      className="relative h-screen w-full overflow-hidden"
      aria-label={t('hero.sectionAria')}
      aria-roledescription="carousel"
    >
      <video
        ref={unlockVideoRef}
        src="/hero-vid.mp4"
        muted
        playsInline
        preload="none"
        className="absolute opacity-0 pointer-events-none"
        style={{ width: '1px', height: '1px' }}
        aria-hidden="true"
      />

      <div className="h-full overflow-hidden" ref={emblaRef}>
        <div className="flex h-full">
          {showHolidayMenusSlide && (
          <div
            className="relative h-full min-w-0 flex-[0_0_100%]"
            role="group"
            aria-roledescription="slide"
          >
            <div className="absolute inset-0">
              <img
                src={HOLIDAY_MENUS_SLIDE_IMAGE}
                alt={t('hero.holidayMenus.imageAlt')}
                className={cn(
                  'h-full w-full object-cover object-center transition-transform duration-[5000ms] ease-out',
                  selectedIndex === 0 ? 'scale-110' : 'scale-100'
                )}
                loading="eager"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/50 to-background z-[5]" />
            </div>

            <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center pt-[73px] md:pt-0">
              <span className="mb-6 text-sm font-light uppercase tracking-[0.3em] text-primary">
                {t('hero.holidayMenus.category')}
              </span>
              <h1 className="sr-only">{t('hero.srTitle')}</h1>
              <p className="mb-6 text-5xl font-light uppercase tracking-[0.15em] text-foreground md:text-7xl">
                {t('hero.holidayMenus.title')}
              </p>
              <p className="mb-10 max-w-xl text-lg font-light text-muted-foreground md:text-xl">
                {t('hero.holidayMenus.subtitle')}
              </p>
              <div className="flex flex-col items-center gap-4 sm:flex-row">
                <Button variant="hero" size="lg" asChild>
                  <LocalizedLink href="/menu-festivi#prenota-festivita" aria-label={t('hero.holidayMenus.ctaAria')}>
                    {t('hero.holidayMenus.cta')}
                  </LocalizedLink>
                </Button>
                <Button variant="ghost" size="lg" asChild>
                  <LocalizedLink href="/menu-festivi">{t('hero.holidayMenus.menuLink')}</LocalizedLink>
                </Button>
              </div>
            </div>
          </div>
          )}

          {showNewMenuSlide && (
          <div
            className="relative h-full min-w-0 flex-[0_0_100%]"
            role="group"
            aria-roledescription="slide"
          >
            <div className="absolute inset-0">
              <img
                src={NEW_MENU_SLIDE_IMAGE}
                alt={t('hero.newMenu.imageAlt')}
                className={cn(
                  'h-full w-full object-cover object-center transition-transform duration-[5000ms] ease-out',
                  selectedIndex === newMenuSlideIndex ? 'scale-110' : 'scale-100'
                )}
                loading="eager"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/50 to-background z-[5]" />
            </div>

            <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center pt-[73px] md:pt-0">
              <span className="mb-6 text-sm font-light uppercase tracking-[0.3em] text-primary">
                {t('hero.newMenu.category')}
              </span>
              {!showHolidayMenusSlide && <h1 className="sr-only">{t('hero.srTitle')}</h1>}
              <p className="mb-6 text-5xl font-light uppercase tracking-[0.15em] text-foreground md:text-7xl">
                {t('hero.newMenu.title')}
              </p>
              <p className="mb-10 max-w-xl text-lg font-light text-muted-foreground md:text-xl">
                {t('hero.newMenu.subtitle')}
              </p>
              <div className="flex flex-col items-center gap-4 sm:flex-row">
                <Button variant="hero" size="lg" asChild>
                  <LocalizedLink href="/prenota" aria-label={t('hero.newMenu.ctaAria')}>
                    {t('hero.newMenu.cta')}
                  </LocalizedLink>
                </Button>
                <Button variant="ghost" size="lg" asChild>
                  <LocalizedLink href="/menu">{t('hero.newMenu.menuLink')}</LocalizedLink>
                </Button>
              </div>
            </div>
          </div>
          )}

          {showExperienceSlide && (
          <div
            className="relative h-full min-w-0 flex-[0_0_100%]"
            role="group"
            aria-roledescription="slide"
          >
            <div className="absolute inset-0">
              <img
                src={experienceSlideImage}
                alt={experienceKey('imageAlt')}
                className={cn(
                  'h-full w-full object-cover object-center transition-transform duration-[5000ms] ease-out',
                  selectedIndex === experienceSlideIndex ? 'scale-110' : 'scale-100'
                )}
                loading="eager"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/50 to-background z-[5]" />
            </div>

            <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center pt-[73px] md:pt-0">
              <span className="mb-6 text-sm font-light uppercase tracking-[0.3em] text-primary">
                {experienceKey('category')}
              </span>
              {!showHolidayMenusSlide && !showNewMenuSlide && <h1 className="sr-only">{t('hero.srTitle')}</h1>}
              <p className="mb-6 text-5xl font-light uppercase tracking-[0.15em] text-foreground md:text-7xl">
                {experienceKey('title')}
              </p>
              <p className="mb-10 max-w-xl text-lg font-light text-muted-foreground md:text-xl">
                {experienceKey('subtitle')}
              </p>
              <Button variant="hero" size="lg" asChild>
                <LocalizedLink href="/experience" aria-label={experienceKey('ctaAria')}>
                  {experienceKey('cta')}
                </LocalizedLink>
              </Button>
            </div>
          </div>
          )}

          {/* Video hero */}
          <div
            className="relative h-full min-w-0 flex-[0_0_100%]"
            role="group"
            aria-roledescription="slide"
          >
            <div className="absolute inset-0">
              <video
                ref={videoRef}
                src="/hero-vid.mp4"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                poster="/img/DSC09272 (2024-04-22T17_55_41.962).JPG"
                webkit-playsinline="true"
                x-webkit-airplay="allow"
                className="h-full w-full object-cover"
                aria-label={t('hero.videoAria')}
                style={{ WebkitPlaysinline: true } as React.CSSProperties}
              />

              <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/50 to-background z-[5]" />

              <div
                className="absolute inset-0 z-[6] md:hidden"
                onTouchStart={(e) => {
                  const video = videoRef.current;
                  if (video && video.paused) {
                    video.play().catch(() => {});
                    e.currentTarget.style.display = 'none';
                  }
                }}
                aria-hidden="true"
              />
            </div>

            <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center pt-[73px] md:pt-0">
              {!showHolidayMenusSlide && !showNewMenuSlide && !showExperienceSlide && <h1 className="sr-only">{t('hero.srTitle')}</h1>}
              {isHoliday ? (
                <>
                  <span className="mb-6 text-sm font-light uppercase tracking-[0.3em] text-primary">
                    {t('hero.holiday.category')}
                  </span>
                  <p className="mb-6 text-5xl font-light uppercase tracking-[0.15em] text-foreground md:text-7xl">
                    {t('hero.holiday.title')}
                  </p>
                  <p className="mb-4 max-w-xl text-lg font-light text-muted-foreground md:text-xl">
                    {t('hero.holiday.subtitle')}
                  </p>
                  <p className="max-w-xl text-base font-light text-muted-foreground md:text-lg">
                    {t('hero.holiday.reopening')}
                  </p>
                </>
              ) : (
                <>
                  <span
                    className="mb-6 text-sm font-light uppercase tracking-[0.3em] text-primary"
                    aria-label={t('hero.categoryAria')}
                  >
                    {t('hero.category')}
                  </span>
                  <p className="mb-10 max-w-xl text-lg font-light text-muted-foreground md:text-xl">
                    {t('hero.line1')}
                    <br />
                    {t('hero.line2')}
                  </p>
                  <Button variant="hero" size="lg" asChild>
                    <LocalizedLink href="/prenota" aria-label={t('common:aria.bookTable')}>
                      {t('common:cta.bookTable')}
                    </LocalizedLink>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {hasMultipleSlides && (
      <div className="absolute bottom-8 left-1/2 z-20 flex -translate-x-1/2 items-center gap-3">
        {Array.from({ length: slideCount }, (_, index) => (
          <button
            key={index}
            type="button"
            onClick={() => scrollTo(index)}
            className={cn(
              'h-2 rounded-full transition-all duration-300',
              selectedIndex === index
                ? 'w-8 bg-primary'
                : 'w-2 bg-foreground/40 hover:bg-foreground/70'
            )}
            aria-label={t('hero.goToSlide', { n: index + 1 })}
            aria-current={selectedIndex === index}
          />
        ))}
      </div>
      )}
    </section>
  );
};
